import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { Trash2 } from 'lucide-react';

import { cn } from '@/utils/classnames';

import { Button } from '../../../button';
// eslint-disable-next-line import/no-cycle
import {
  type ActionButtonUpdateAttributes,
  ACTION_BUTTON_NODE_NAME,
  ActionButtonAttributes,
} from '.';

import type React from 'react';

function toTailwindJustify(alignment: string | undefined) {
  switch (alignment) {
    case 'left':
      return 'justify-start';
    case 'center':
      return 'justify-center';
    case 'right':
      return 'justify-end';
    default:
      return 'justify-center';
  }
}

interface IconButtonProps {
  icon: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'destructive';
}

function IconButton({ icon, onClick, variant = 'default' }: IconButtonProps) {
  return (
    <Button
      size="icon"
      variant="outline"
      className={cn(
        'h-8 w-8 rounded-full shadow-sm',
        variant === 'destructive' &&
          'border-red-300 text-red-600 hover:bg-red-50',
      )}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
    >
      {icon}
    </Button>
  );
}

export function ActionButtonNodeView({
  editor,
  deleteNode,
  node,
  selected,
  getPos,
  updateAttributes,
}: NodeViewProps) {
  const attrs = node.attrs as ActionButtonAttributes;

  const handleClick = () => {
    const ext = editor.extensionManager.extensions.find(
      e => e.name === ACTION_BUTTON_NODE_NAME,
    ) as
      | {
          options?: {
            onClick?: (
              a: ActionButtonAttributes,
              helpers: { updateAttributes: ActionButtonUpdateAttributes },
            ) => void;
          };
        }
      | undefined;

    const safeUpdateAttributes: ActionButtonUpdateAttributes = nextAttrs => {
      updateAttributes(nextAttrs);
    };

    ext?.options?.onClick?.(attrs, {
      updateAttributes: safeUpdateAttributes,
    });
  };

  return (
    <NodeViewWrapper
      as="div"
      contentEditable={false}
      data-drag-handle=""
      draggable="true"
      onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
        // Ensure this node becomes selected so the action UI renders.
        e.preventDefault();
        e.stopPropagation();

        const pos = typeof getPos === 'function' ? getPos() : null;
        if (typeof pos === 'number') {
          editor.chain().focus().setNodeSelection(pos).run();
        }
      }}
      onClick={handleClick}
      className={cn(
        // Full-width button by default; when selected, reserve left padding for controls
        // so the button "shrinks" and the control appears to the left.
        'relative w-full rounded border border-transparent py-2 transition-all',
        'cursor-pointer hover:bg-gray-50',
        selected ? 'pl-12' : 'pl-0',
        selected && 'border-blue-500 bg-blue-50 shadow-md',
      )}
    >
      {selected && (
        <div className="absolute top-1/2 left-2 -translate-y-1/2">
          <IconButton
            icon={<Trash2 className="h-4 w-4" />}
            onClick={deleteNode}
            variant="destructive"
          />
        </div>
      )}

      <div className={cn('flex w-full', toTailwindJustify(attrs.alignment))}>
        <button
          type="button"
          className={cn(
            'rounded px-6 py-3 text-center font-medium transition-all',
            'pointer-events-none',
            attrs.fullWidth ? 'block w-full' : 'inline-block w-fit',
          )}
          style={{
            borderRadius: attrs.borderRadius,
            color: attrs.textColor,
            backgroundColor: attrs.backgroundColor,
          }}
        >
          {attrs.value}
        </button>
      </div>
    </NodeViewWrapper>
  );
}
